import {
  LocalStorageService,
  LocalStorageKey,
  SessionStorageService,
  SessionStorageKey,
} from '@template/utils';
import { useAccountStore, resetAccountSpecificData } from '@template/stores';
import { useGlobalWebSocket } from '@/composables/useGlobalWebSocket';
import { resetAxiosInterceptorState } from '@/modules/axios';
import { useUserStore } from '@/stores/useUserStore';
import { authService } from './api';
import router from '@/router';

/**
 * 로그아웃 API 호출 후 클라이언트에서 처리해야할 작업을 수행합니다.
 */
export const Logout = async () => {
  try {
    await authService.logout();
  } catch (error) {
    console.error('로그아웃 API 호출 실패:', error);
    // API 호출 실패해도 로그아웃 처리는 진행
  } finally {
    // API 호출 성공/실패 여부와 관계없이 항상 토큰 제거
    await LogoutTask();
  }
};

/**
 * 계정 관련 초기화
 * 로그아웃 시 계정 레벨의 데이터를 초기화합니다.
 */
const resetAccountData = async (): Promise<void> => {
  // 1. LocalStorage 정리
  LocalStorageService.removeItem(LocalStorageKey.ACCESS_TOKEN);
  const accountStore = useAccountStore();
  accountStore.setAccountSimpleAuthEnabled(false);

  // 2. Axios 인터셉터 상태 초기화
  try {
    resetAxiosInterceptorState();
  } catch (error) {
    console.error('[resetAccountData] Axios 인터셉터 상태 초기화 실패:', error);
  }

  // 3. WebSocket 연결 해제
  try {
    const { disconnectGlobalWebSocket } = useGlobalWebSocket();
    await disconnectGlobalWebSocket();
    // WebsocketService의 onClose에서 Access Token 확인 후 재연결 여부 결정
  } catch (error) {
    console.error('[resetAccountData] WebSocket 연결 해제 실패:', error);
  }

  // 4. AccountStore 초기화 (계좌 목록, 계좌 비밀번호 실패 횟수 등)
  try {
    const accountStore = useAccountStore();
    accountStore.resetAccountList();
    accountStore.resetAccountPasswordFailedCount();
  } catch (error) {
    console.error('[resetAccountData] AccountStore 초기화 실패:', error);
  }

  // 5. UserStore의 로그인 상태 업데이트
  const userStore = useUserStore();
  userStore.updateLoginStatus();

  // 6. 로그인 페이지로 이동
  if (router.currentRoute.value.name !== 'login') {
    await router.replace({ name: 'login' });
  }
};

/**
 * 로그아웃 시 클라이언트에서 처리해야할 작업을 수행합니다.
 * -----------------------------------------------------
 * 로그아웃 API 호출은 필요 없고
 * AccessToken 정보가 클라이언트에 남아있어 에러가 발생할 때
 * 수행하여 로그아웃 상태로 만듭니다.
 * -----------------------------------------------------
 * 사용 Case
 * - 401 - S0003 에러 발생 시 (로그아웃이 필요한 요청)
 * - 토큰 갱신 요청이 500 에러인 경우
 * - AccessToken이 존재하지 않을 경우
 */
export const LogoutTask = async () => {
  try {
    // 계좌별 데이터 초기화
    resetAccountSpecificData();

    // LocalStorage 정리
    resetLocalStorage();

    // SessionStorage 정리 (로그아웃 시 allowlist 방식)
    resetSessionStorage();

    // 계정 관련 초기화
    await resetAccountData();
  } catch (error) {
    console.error('[LogoutTask] 로그아웃 처리 중 오류:', error);
    // 오류가 발생해도 로그인 페이지로 이동 시도
    try {
      if (router.currentRoute.value.name !== 'login') {
        await router.replace({ name: 'login' });
      }
    } catch (routerError) {
      console.error('[LogoutTask] 라우터 이동 실패:', routerError);
    }
  }
};

/**
 * LocalStorage 정리 (로그아웃 시 호출)
 */
export const resetLocalStorage = () => {
  LocalStorageService.removeItem(LocalStorageKey.ACCOUNT_PASSWORD_POPUP_NEEDED);
  LocalStorageService.removeItem(LocalStorageKey.ACCOUNT_TOKEN);
  LocalStorageService.removeItem(LocalStorageKey.TRADE_MODE);
};

/**
 * SessionStorage 정리 (로그아웃 시 호출)
 * - 세션 유지 목적의 화면 필터/검색어만 allowlist로 제거합니다.
 */
export const resetSessionStorage = () => {
  SessionStorageService.removeItem(SessionStorageKey.SYMBOL_LIST_SEARCH_QUERY);
  SessionStorageService.removeItem(SessionStorageKey.ORDER_SCREEN_FILTERS_WTS);
  SessionStorageService.removeItem(SessionStorageKey.ORDER_SCREEN_FILTERS_MTS);
  SessionStorageService.removeItem(SessionStorageKey.TRADE_SCREEN_FILTERS);
};
